import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Printer, TrendingUp, Users, Smile, Percent, Receipt, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { KpiCard, PageHeader } from "@/components/KpiCard";
import { useResource } from "@/lib/api";
import { METHODS, STATUS, STATUS_KEYS, brl, monthLabel } from "@/lib/constants";

const BLUES = ["#0284C7", "#38BDF8", "#0E7490", "#7DD3FC", "#1E3A8A", "#22D3EE", "#64748B", "#0EA5E9", "#BAE6FD", "#155E75", "#94A3B8", "#2563EB", "#A5F3FC"];

const ChartCard = ({ title, children, testId }) => (
  <div className="surface p-5" data-testid={testId}>
    <h3 className="font-display mb-4 font-bold text-slate-900">{title}</h3>
    <div className="h-72">{children}</div>
  </div>
);

export default function Reports() {
  const { data } = useResource("/reports");
  if (!data) return <div className="h-40 animate-pulse rounded-2xl bg-sky-50" />;
  const k = data.kpis;
  const months = data.months.map((m) => ({ ...m, label: monthLabel(m.month) }));
  const methods = Object.entries(data.methods).filter(([, v]) => v > 0).map(([m, v]) => ({ name: METHODS[m], value: v }));
  const cats = Object.entries(data.categories).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const tick = { fontSize: 11, fill: "#64748B" };

  return (
    <div>
      <PageHeader title="Relatórios" subtitle="Indicadores dos últimos 12 meses">
        <Button variant="outline" className="no-print rounded-full" onClick={() => window.print()} data-testid="reports-print-btn"><Printer className="mr-1.5 h-4 w-4" /> Imprimir / PDF</Button>
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
        <KpiCard label="Faturamento 12m" value={brl(k.revenue_12m)} icon={TrendingUp} tone="emerald" testId="rep-kpi-revenue" />
        <KpiCard label="Ticket médio" value={brl(k.avg_ticket)} icon={Receipt} testId="rep-kpi-ticket" />
        <KpiCard label="Pacientes" value={k.total_patients} icon={Users} testId="rep-kpi-patients" />
        <KpiCard label="Com aparelho" value={k.ortho_active} icon={Smile} tone="cyan" testId="rep-kpi-ortho" />
        <KpiCard label="Taxa de faltas" value={`${k.no_show_rate}%`} icon={Percent} tone="amber" testId="rep-kpi-noshow" />
        <KpiCard label="Inadimplência" value={brl(k.overdue)} icon={AlertCircle} tone="rose" testId="rep-kpi-overdue" />
      </div>
      <div className="mt-6 grid gap-5 xl:grid-cols-2">
        <ChartCard title="Faturamento mensal" testId="chart-revenue">
          <ResponsiveContainer>
            <BarChart data={months}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
              <XAxis dataKey="label" tick={tick} axisLine={false} tickLine={false} />
              <YAxis tick={tick} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`} />
              <Tooltip formatter={(v) => brl(v)} cursor={{ fill: "#F0F9FF" }} />
              <Bar dataKey="revenue" name="Faturamento" fill="#0284C7" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Comparecimento da agenda por mês" testId="chart-attendance">
          <ResponsiveContainer>
            <BarChart data={months}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
              <XAxis dataKey="label" tick={tick} axisLine={false} tickLine={false} />
              <YAxis tick={tick} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#F0F9FF" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {STATUS_KEYS.map((s) => <Bar key={s} dataKey={s} name={STATUS[s].label} stackId="a" fill={STATUS[s].hex} />)}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Recebimentos por forma de pagamento" testId="chart-methods">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={methods} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                {methods.map((_, i) => <Cell key={i} fill={BLUES[i % BLUES.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => brl(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Tratamentos realizados por tipo" testId="chart-treatments">
          <ResponsiveContainer>
            <BarChart data={cats} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" horizontal={false} />
              <XAxis type="number" tick={tick} allowDecimals={false} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={tick} width={120} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#F0F9FF" }} />
              <Bar dataKey="value" name="Quantidade" fill="#38BDF8" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <div className="surface mt-5 overflow-x-auto p-5" data-testid="reports-table">
        <h3 className="font-display mb-3 font-bold text-slate-900">Resumo mensal</h3>
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-400">
            <tr><th className="py-2">Mês</th><th className="text-right">Faturamento</th><th className="text-right">Novos pacientes</th><th className="text-right">Concluídas</th><th className="text-right">Faltas</th><th className="text-right">Reagendadas</th></tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month} className="font-mono-num border-t border-slate-100">
                <td className="py-2 font-sans capitalize text-slate-700">{m.label}</td>
                <td className="text-right font-semibold">{brl(m.revenue)}</td>
                <td className="text-right">{m.new_patients}</td>
                <td className="text-right">{m.concluido}</td>
                <td className="text-right">{m.faltou}</td>
                <td className="text-right">{m.reagendado}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
